/**
 * 角色权限页 ADM-26（staff-rbac-wechat-login:SC-47~SC-48）。
 *
 * 对齐原型 prototype/admin/src/pages/org/RolePage.tsx：
 * - 角色列表：名称、适用范围、成员数（后端派生）、状态、操作（配置/员工授权/复制/停用启用）
 * - 权限矩阵配置 Modal：角色名编辑 + 菜单键 × 动作（勾选）× 数据范围（下拉）
 * - 员工授权 Modal：按角色增删成员（PUT /staffs/:id/grants，见 components/RoleMembersModal）
 */
import { useCallback, useEffect, useState } from "react";
import {
  App,
  Button,
  Card,
  Checkbox,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { DraggableModal } from "../../components/DraggableModal";
import {
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { EllipsisLink } from "../../components/EllipsisCell";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import { useCampusOptions } from "../../hooks/useCampusOptions";
import {
  dataScopeOptions,
  permissionActionLabel,
  roleApi,
  roleScopeLabel,
  roleStatusLabel,
  type MenuKeyCatalogItem,
  type PermissionAction,
  type RoleDetail,
  type RolePermission,
  type RoleStatus,
} from "../../api/role.api";
import { ApiError } from "../../api/transport";
import { RoleMembersModal } from "./components/RoleMembersModal";
import { HasPermission, useHasPermission } from "../../auth/HasPermission";
import { useMemo } from "react";
import { useSession } from "../../auth/SessionContext";

const { Title, Text } = Typography;

/** 提取错误信息 */
function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "操作失败";
}

/** 权限矩阵本地编辑态（当前与 RolePermission 同构，保留类型名以便后续扩展编辑字段） */
type EditablePermission = RolePermission;

export function RolePage() {
  const { message: msg } = App.useApp();
  const { refreshPermissions } = useSession();
  const [roles, setRoles] = useState<RoleDetail[]>([]);
  const [loading, setLoading] = useState(false);
  // 校区选项（员工授权弹窗用）经共享 hook：ACTIVE + 按数据范围过滤 + numericCode 排序，收编自拉口径；
  // 授权校区多选的现值回填按弹窗业务语义自理，不硬套单选默认
  const { campuses } = useCampusOptions();
  const [menuCatalog, setMenuCatalog] = useState<MenuKeyCatalogItem[]>([]);
  /** 目录映射（menuKey → 目录项）：矩阵展示名与可配置动作以 menu-keys 目录为准 */
  const catalogByKey = useMemo(
    () => new Map(menuCatalog.map((c) => [c.menuKey, c])),
    [menuCatalog],
  );

  // 权限矩阵配置弹窗状态
  const [editingRole, setEditingRole] = useState<RoleDetail | null>(null);
  const [editName, setEditName] = useState("");
  const [editPermissions, setEditPermissions] = useState<EditablePermission[]>(
    [],
  );
  const [saving, setSaving] = useState(false);

  // 员工授权弹窗状态
  const [grantingRole, setGrantingRole] = useState<RoleDetail | null>(null);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const list = await roleApi.listRoles();
      setRoles(list);
    } catch (err) {
      msg.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [msg]);

  // 加载菜单键目录（权限矩阵新增行选项源）；校区选项已改经共享 hook useCampusOptions
  useEffect(() => {
    void (async () => {
      try {
        const catalog = await roleApi.listMenuKeys();
        setMenuCatalog(catalog);
      } catch {
        // 目录加载失败不阻塞角色列表，仅影响新增行的可选项
      }
    })();
  }, []);

  useEffect(() => {
    void fetchRoles();
  }, [fetchRoles]);

  /** 打开权限矩阵配置 */
  function openConfig(role: RoleDetail) {
    setEditingRole(role);
    setEditName(role.name);
    // 编辑态取目录交集进入：存量行的无效动作（无检查点）自然清洗，保存后不再回写
    setEditPermissions(
      role.permissions
        ? role.permissions.map((p) => ({
            ...p,
            actions: p.actions.filter((a) =>
              catalogByKey
                .get(p.menuKey)
                ?.availableActions.includes(a as PermissionAction),
            ),
          }))
        : [],
    );
  }

  /** 切换某权限项的动作勾选 */
  function toggleAction(perm: EditablePermission, action: string) {
    setEditPermissions((prev) =>
      prev.map((p) =>
        p.menuKey === perm.menuKey
          ? {
              ...p,
              actions: p.actions.includes(action)
                ? p.actions.filter((a) => a !== action)
                : [...p.actions, action],
            }
          : p,
      ),
    );
  }

  /** 修改某权限项的数据范围 */
  function changeScope(perm: EditablePermission, dataScope: string) {
    setEditPermissions((prev) =>
      prev.map((p) =>
        p.menuKey === perm.menuKey
          ? { ...p, dataScope: dataScope as RolePermission["dataScope"] }
          : p,
      ),
    );
  }

  /** 新增菜单键权限行（已存在则忽略；默认 VIEW + 授权校区） */
  function addMenuKey(menuKey: string) {
    const item = menuCatalog.find((c) => c.menuKey === menuKey);
    if (!item) return;
    setEditPermissions((prev) =>
      prev.some((p) => p.menuKey === menuKey)
        ? prev
        : [
            ...prev,
            {
              menuKey,
              menuLabel: item.menuLabel,
              actions: ["VIEW"],
              dataScope: "GRANTED_CAMPUS",
            },
          ],
    );
  }

  /** 删除菜单键权限行 */
  function removeMenuKey(menuKey: string) {
    setEditPermissions((prev) => prev.filter((p) => p.menuKey !== menuKey));
  }

  /** 保存权限矩阵（PATCH /roles/:id） */
  async function saveConfig() {
    if (!editingRole) return;
    setSaving(true);
    try {
      await roleApi.updateRole(editingRole.id, {
        name: editName,
        permissions: editPermissions,
      });
      msg.success("权限已保存");
      setEditingRole(null);
      void fetchRoles();
      // 修改了自身所属角色的权限矩阵时即时生效，免整页刷新（R16 体验项）
      void refreshPermissions();
    } catch (err) {
      if (err instanceof ApiError) msg.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  /** 新增角色 */
  async function createRole() {
    try {
      await roleApi.createRole({ name: "新角色", scope: "CAMPUS" });
      msg.success("已新增角色，请配置权限矩阵");
      void fetchRoles();
    } catch (err) {
      msg.error(errorMessage(err));
    }
  }

  /** 复制角色（新建同名副本） */
  async function copyRole(role: RoleDetail) {
    try {
      await roleApi.createRole({
        name: `${role.name}（副本）`,
        scope: role.scope,
      });
      msg.success("已复制角色");
      void fetchRoles();
    } catch (err) {
      msg.error(errorMessage(err));
    }
  }

  /** 切换角色状态 */
  async function toggleStatus(role: RoleDetail) {
    const next: RoleStatus = role.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
    try {
      await roleApi.updateRole(role.id, { status: next });
      msg.success(next === "ACTIVE" ? "已启用" : "已停用");
      void fetchRoles();
    } catch (err) {
      msg.error(errorMessage(err));
    }
  }

  // 行操作闸控（对齐后端 @Roles）：配置/停用启用=ADM-26 EDIT、复制=ADM-26 CREATE、员工授权走员工端点=ADM-25 EDIT
  const canManageRole = useHasPermission("ADM-26", "EDIT");
  const canCreateRole = useHasPermission("ADM-26", "CREATE");
  const canGrantStaff = useHasPermission("ADM-25", "EDIT");
  const { columns, components } = useResizableColumns<RoleDetail>([
    {
      title: "角色名称",
      dataIndex: "name",
      ellipsis: true,
      render: (v: string, r) => (
        <EllipsisLink value={v} onClick={() => openConfig(r)} />
      ),
    },
    {
      title: "适用范围",
      dataIndex: "scope",
      width: 120,
      render: (v: RoleDetail["scope"]) => roleScopeLabel[v] ?? v,
    },
    { title: "成员数", dataIndex: "memberCount", width: 90, align: "center" },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (v: RoleStatus) => (
        <Tag color={v === "ACTIVE" ? "green" : "default"}>
          {roleStatusLabel[v] ?? v}
        </Tag>
      ),
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      width: 160,
      render: (v?: string | null) =>
        v ? new Date(v).toLocaleString("zh-CN") : "-",
    },
    {
      title: "操作",
      fixed: "right",
      key: "action",
      width: 280,
      render: (_, r) => (
        <Space size="small">
          {canManageRole && (
            <Button type="link" size="small" onClick={() => openConfig(r)}>
              配置
            </Button>
          )}
          {canGrantStaff && (
            <Button type="link" size="small" onClick={() => setGrantingRole(r)}>
              员工授权
            </Button>
          )}
          {canCreateRole && (
            <Button type="link" size="small" onClick={() => void copyRole(r)}>
              复制
            </Button>
          )}
          {canManageRole && (
            <Popconfirm
              title={
                r.status === "ACTIVE" ? "确认停用该角色？" : "确认启用该角色？"
              }
              onConfirm={() => void toggleStatus(r)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" size="small" danger={r.status === "ACTIVE"}>
                {r.status === "ACTIVE" ? "停用" : "启用"}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]);

  return (
    <div>
      <Title level={4}>角色权限</Title>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space>
            {/* 无 ADM-26 CREATE 权限不渲染入口，越权直调由后端 403 兜底 */}
            <HasPermission menuKey="ADM-26" action="CREATE">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => void createRole()}
              >
                新增角色
              </Button>
            </HasPermission>
            <Button icon={<ReloadOutlined />} onClick={() => void fetchRoles()}>
              刷新
            </Button>
          </Space>
        </div>
        <Table<RoleDetail>
          scroll={{ x: "max-content" }}
          rowKey="id"
          columns={columns}
          components={components}
          dataSource={roles}
          loading={loading}
          pagination={false}
        />
      </Card>

      {/* 权限矩阵配置 Modal */}
      <DraggableModal
        open={!!editingRole}
        title={editingRole ? `权限矩阵 · ${editingRole.name}` : "权限矩阵"}
        onCancel={() => setEditingRole(null)}
        onOk={() => void saveConfig()}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        destroyOnHidden
        width={760}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>
            角色名称
          </Text>
          <Input
            aria-label="角色名称"
            placeholder="请输入角色名称"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
        </div>
        <Select
          placeholder="添加菜单键"
          showSearch
          optionFilterProp="label"
          value={null}
          style={{ width: 280, marginBottom: 16, display: "block" }}
          options={menuCatalog
            .filter(
              (c) => !editPermissions.some((p) => p.menuKey === c.menuKey),
            )
            .map((c) => ({
              value: c.menuKey,
              label: `${c.menuLabel}（${c.menuKey}）`,
            }))}
          onSelect={(value) => addMenuKey(String(value))}
        />
        <Table<EditablePermission>
          scroll={{ x: "max-content" }}
          rowKey="menuKey"
          dataSource={editPermissions}
          pagination={false}
          size="small"
          columns={[
            {
              title: "菜单",
              dataIndex: "menuLabel",
              width: 200,
              // 同名页面键（排课工作台×3 等）带键码消歧
              render: (_, perm) =>
                `${catalogByKey.get(perm.menuKey)?.menuLabel ?? perm.menuLabel}（${perm.menuKey}）`,
            },
            {
              title: "动作",
              render: (_, perm) => (
                <Space wrap>
                  {(
                    catalogByKey.get(perm.menuKey)?.availableActions ?? ["VIEW"]
                  ).map((action: PermissionAction) => (
                    <Checkbox
                      key={action}
                      checked={perm.actions.includes(action)}
                      onChange={() => toggleAction(perm, action)}
                    >
                      {permissionActionLabel[action] ?? action}
                    </Checkbox>
                  ))}
                </Space>
              ),
            },
            {
              title: "数据范围",
              dataIndex: "dataScope",
              width: 130,
              render: (v: string, perm) => (
                <Select
                  size="small"
                  style={{ width: 110 }}
                  value={v}
                  options={dataScopeOptions}
                  onChange={(value) => changeScope(perm, value)}
                />
              ),
            },
            {
              title: "操作",
              key: "rowAction",
              width: 70,
              render: (_, perm) => (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeMenuKey(perm.menuKey)}
                >
                  删除
                </Button>
              ),
            },
          ]}
        />
      </DraggableModal>

      {/* 员工授权弹窗（增删成功后刷新角色列表的成员数） */}
      {grantingRole && (
        <RoleMembersModal
          role={grantingRole}
          campuses={campuses}
          onClose={() => setGrantingRole(null)}
          onMutated={() => void fetchRoles()}
        />
      )}
    </div>
  );
}
